import { getMoneyMerchantDisplayName, normaliseMoneyMerchant } from '../money';

export type SharedHomeRoutineKind =
  | 'houseplants'
  | 'lawn'
  | 'change-bedding'
  | 'change-towels'
  | 'laundry'
  | 'ironing'
  | 'clean-bathrooms'
  | 'clean-surfaces'
  | 'vacuum-mop-floors'
  | 'clean-kitchen'
  | 'clean-oven'
  | 'clean-fridge'
  | 'windows-mirrors-picture-frames'
  | 'wash-car'
  | 'valet-inside-car'
  | 'hoe-weed-garden';

export type SharedHomeBillKey =
  | 'council-tax'
  | 'gas'
  | 'electricity'
  | 'broadband'
  | 'mobile-phone'
  | 'car-tax'
  | 'car-service'
  | 'car-mot'
  | 'mortgage'
  | 'rent'
  | 'service-charge'
  | 'ground-rent'
  | 'water'
  | 'child-care-education'
  | 'child-maintenance'
  | 'gym-membership'
  | 'sports-leisure-clubs';

export type SharedHomeBillFrequency = 'weekly' | 'monthly' | 'annual';
export type SharedHomeBillWeekday =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday';
export type SharedHomeRoutineFrequency = 'weekly' | 'fortnightly' | 'monthly';

export type SharedHomeBillDefinition = {
  key: SharedHomeBillKey;
  label: string;
  defaultFrequency: SharedHomeBillFrequency;
  vehicleLinked?: boolean;
};

export type SharedHomeBillRecord = {
  billKey: SharedHomeBillKey;
  enabled: boolean;
  frequency: SharedHomeBillFrequency;
  amount: number | null;
  dueDayOfWeek: SharedHomeBillWeekday | null;
  dueDayOfMonth: number | null;
  annualDueDate: string | null;
  providerName: string | null;
  providerAccount: string | null;
};

export type SharedHomeBillSeedContext = {
  tenure: string | null;
  monthlyHousingCost: number | null;
  mortgageProvider: string | null;
  landlordName: string | null;
  councilTaxBand: string | null;
  groundRentAmount: number | null;
  serviceChargeAmount: number | null;
  hasVehicle: boolean | null;
  vehicleRegistrationNumber: string | null;
  vehicleTaxDueDate: string | null;
  vehicleMotDueDate: string | null;
  utilityGasProvider: string | null;
  utilityElectricProvider: string | null;
  utilityBroadbandProvider: string | null;
  utilityWaterProvider: string | null;
  utilityPhoneProvider: string | null;
};

export type SharedDvlaVehicleLookupResult = {
  registrationNumber: string;
  make: string | null;
  colour: string | null;
  fuelType: string | null;
  motStatus: string | null;
  motExpiryDate: string | null;
  taxStatus: string | null;
  taxDueDate: string | null;
  yearOfManufacture: number | null;
  monthOfFirstRegistration: string | null;
  co2Emissions: number | null;
  engineCapacity: number | null;
  generatedAt: string;
};

export type SharedHomeRoutineCadence = {
  days: number;
  label: string;
  summary: string;
};

export type SharedHomeRoutineSettings = {
  frequency: SharedHomeRoutineFrequency;
  reminderDayOfWeek: SharedHomeBillWeekday;
  notificationsEnabled: boolean;
  calendarSyncEnabled: boolean;
};

export type SharedHomeRoutineMeta = {
  title: string;
  inactiveBody: string;
  enableLabel: string;
  subtitle: string;
  emoji: string;
};

export type SharedRenewalConnectionType =
  | 'energy'
  | 'gas'
  | 'electric'
  | 'broadband'
  | 'phone'
  | 'water'
  | 'insurance'
  | 'mortgage'
  | 'tenancy'
  | 'council-tax'
  | 'other';

export type SharedHomeBillProviderDefinition = {
  label: string;
  portalUrl: string;
  billKeys: SharedHomeBillKey[];
  keywords: string[];
};

export type SharedHomeBillInferenceInput = {
  bookingDate: string;
  amount: number;
  description?: string | null;
  creditorName?: string | null;
  accountInstitutionName?: string | null;
};

export type SharedHomeBillInference = {
  billKey: SharedHomeBillKey;
  merchantKey: string | null;
  frequency: SharedHomeBillFrequency;
  amount: number | null;
  dueDayOfWeek: SharedHomeBillWeekday | null;
  dueDayOfMonth: number | null;
  annualDueDate: string | null;
  providerName: string | null;
  summary: string;
};

export type SharedHomeBillTransferCandidate = {
  billKey: SharedHomeBillKey;
  label: string;
  currentBill: SharedHomeBillRecord;
  suggestedBill: SharedHomeBillRecord;
  summary: string;
  merchantKey: string | null;
};

export const SHARED_RENEWAL_CONNECTION_OPTIONS: Array<{ value: SharedRenewalConnectionType; label: string }> = [
  { value: 'energy', label: 'Energy account' },
  { value: 'gas', label: 'Gas account' },
  { value: 'electric', label: 'Electric account' },
  { value: 'broadband', label: 'Broadband account' },
  { value: 'phone', label: 'Phone account' },
  { value: 'water', label: 'Water account' },
  { value: 'insurance', label: 'Insurance account' },
  { value: 'mortgage', label: 'Mortgage account' },
  { value: 'tenancy', label: 'Tenancy / landlord portal' },
  { value: 'council-tax', label: 'Council tax account' },
  { value: 'other', label: 'Other provider account' },
];

export const SHARED_HOME_BILL_DEFINITIONS: SharedHomeBillDefinition[] = [
  { key: 'council-tax', label: 'Council Tax', defaultFrequency: 'monthly' },
  { key: 'gas', label: 'Gas', defaultFrequency: 'monthly' },
  { key: 'electricity', label: 'Electricity', defaultFrequency: 'monthly' },
  { key: 'broadband', label: 'Broadband', defaultFrequency: 'monthly' },
  { key: 'mobile-phone', label: 'Mobile Phone', defaultFrequency: 'monthly' },
  { key: 'car-tax', label: 'Car Tax', defaultFrequency: 'annual', vehicleLinked: true },
  { key: 'car-service', label: 'Car Service', defaultFrequency: 'annual', vehicleLinked: true },
  { key: 'car-mot', label: 'Car MOT', defaultFrequency: 'annual', vehicleLinked: true },
  { key: 'mortgage', label: 'Mortgage', defaultFrequency: 'monthly' },
  { key: 'rent', label: 'Rent', defaultFrequency: 'monthly' },
  { key: 'service-charge', label: 'Service Charge', defaultFrequency: 'monthly' },
  { key: 'ground-rent', label: 'Ground Rent', defaultFrequency: 'annual' },
  { key: 'water', label: 'Water', defaultFrequency: 'monthly' },
  { key: 'child-care-education', label: 'Child Care / Education', defaultFrequency: 'monthly' },
  { key: 'child-maintenance', label: 'Child Maintenance', defaultFrequency: 'monthly' },
  { key: 'gym-membership', label: 'Gym Membership', defaultFrequency: 'monthly' },
  { key: 'sports-leisure-clubs', label: 'Sports / Leisure Clubs', defaultFrequency: 'monthly' },
];

export const SHARED_HOME_BILL_FREQUENCY_OPTIONS: Array<{ value: SharedHomeBillFrequency; label: string }> = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
];

export const SHARED_HOME_BILL_WEEKDAY_OPTIONS: SharedHomeBillWeekday[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const SHARED_HOME_ROUTINE_FREQUENCY_OPTIONS: Array<{ value: SharedHomeRoutineFrequency; label: string }> = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
];

export const SHARED_HOME_ROUTINE_KINDS: SharedHomeRoutineKind[] = [
  'change-bedding',
  'change-towels',
  'laundry',
  'ironing',
  'clean-bathrooms',
  'clean-surfaces',
  'vacuum-mop-floors',
  'clean-kitchen',
  'clean-oven',
  'clean-fridge',
  'windows-mirrors-picture-frames',
  'wash-car',
  'valet-inside-car',
  'hoe-weed-garden',
  'houseplants',
  'lawn',
];

const SHARED_HOME_BILL_PROVIDER_CATALOG: SharedHomeBillProviderDefinition[] = [
  {
    label: 'British Gas',
    portalUrl: 'https://www.britishgas.co.uk/',
    billKeys: ['gas', 'electricity'],
    keywords: ['british gas'],
  },
  {
    label: 'Octopus Energy',
    portalUrl: 'https://octopus.energy/',
    billKeys: ['gas', 'electricity'],
    keywords: ['octopus'],
  },
  {
    label: 'EDF Energy',
    portalUrl: 'https://www.edfenergy.com/',
    billKeys: ['gas', 'electricity'],
    keywords: ['edf'],
  },
  {
    label: 'E.ON Next',
    portalUrl: 'https://www.eonnext.com/',
    billKeys: ['gas', 'electricity'],
    keywords: ['e on next', 'eon next', 'e on', 'eon'],
  },
  {
    label: 'OVO Energy',
    portalUrl: 'https://www.ovoenergy.com/',
    billKeys: ['gas', 'electricity'],
    keywords: ['ovo'],
  },
  {
    label: 'Scottish Power',
    portalUrl: 'https://www.scottishpower.co.uk/',
    billKeys: ['gas', 'electricity'],
    keywords: ['scottish power'],
  },
  {
    label: 'Utilita',
    portalUrl: 'https://www.utilita.co.uk/',
    billKeys: ['gas', 'electricity'],
    keywords: ['utilita'],
  },
  {
    label: 'Thames Water',
    portalUrl: 'https://www.thameswater.co.uk/',
    billKeys: ['water'],
    keywords: ['thames water'],
  },
  {
    label: 'Severn Trent',
    portalUrl: 'https://www.stwater.co.uk/',
    billKeys: ['water'],
    keywords: ['severn trent', 'stwater'],
  },
  {
    label: 'United Utilities',
    portalUrl: 'https://www.unitedutilities.com/',
    billKeys: ['water'],
    keywords: ['united utilities'],
  },
  {
    label: 'Yorkshire Water',
    portalUrl: 'https://www.yorkshirewater.com/',
    billKeys: ['water'],
    keywords: ['yorkshire water'],
  },
  {
    label: 'Anglian Water',
    portalUrl: 'https://www.anglianwater.co.uk/',
    billKeys: ['water'],
    keywords: ['anglian water'],
  },
  {
    label: 'Southern Water',
    portalUrl: 'https://www.southernwater.co.uk/',
    billKeys: ['water'],
    keywords: ['southern water'],
  },
  {
    label: 'South West Water',
    portalUrl: 'https://www.southwestwater.co.uk/',
    billKeys: ['water'],
    keywords: ['south west water', 'southwestwater'],
  },
  {
    label: 'Welsh Water',
    portalUrl: 'https://www.dwrcymru.com/',
    billKeys: ['water'],
    keywords: ['welsh water', 'dwr cymru'],
  },
  {
    label: 'Wessex Water',
    portalUrl: 'https://www.wessexwater.co.uk/',
    billKeys: ['water'],
    keywords: ['wessex water'],
  },
  {
    label: 'Northumbrian Water',
    portalUrl: 'https://www.nwl.co.uk/',
    billKeys: ['water'],
    keywords: ['northumbrian water', 'essex and suffolk water'],
  },
  {
    label: 'BT',
    portalUrl: 'https://www.bt.com/',
    billKeys: ['broadband', 'mobile-phone'],
    keywords: ['bt'],
  },
  {
    label: 'Sky',
    portalUrl: 'https://www.sky.com/',
    billKeys: ['broadband', 'mobile-phone'],
    keywords: ['sky'],
  },
  {
    label: 'Virgin Media',
    portalUrl: 'https://www.virginmedia.com/',
    billKeys: ['broadband', 'mobile-phone'],
    keywords: ['virgin media'],
  },
  {
    label: 'TalkTalk',
    portalUrl: 'https://www.talktalk.co.uk/',
    billKeys: ['broadband'],
    keywords: ['talktalk'],
  },
  {
    label: 'Vodafone',
    portalUrl: 'https://www.vodafone.co.uk/',
    billKeys: ['broadband', 'mobile-phone'],
    keywords: ['vodafone'],
  },
  {
    label: 'EE',
    portalUrl: 'https://ee.co.uk/',
    billKeys: ['broadband', 'mobile-phone'],
    keywords: ['ee'],
  },
  {
    label: 'O2',
    portalUrl: 'https://www.o2.co.uk/',
    billKeys: ['mobile-phone'],
    keywords: ['o2'],
  },
  {
    label: 'Three',
    portalUrl: 'https://www.three.co.uk/',
    billKeys: ['mobile-phone'],
    keywords: ['three', '3 mobile'],
  },
  {
    label: 'Plusnet',
    portalUrl: 'https://www.plus.net/',
    billKeys: ['broadband', 'mobile-phone'],
    keywords: ['plusnet'],
  },
  {
    label: 'Nationwide',
    portalUrl: 'https://www.nationwide.co.uk/',
    billKeys: ['mortgage'],
    keywords: ['nationwide'],
  },
  {
    label: 'Halifax',
    portalUrl: 'https://www.halifax.co.uk/',
    billKeys: ['mortgage'],
    keywords: ['halifax'],
  },
  {
    label: 'Santander',
    portalUrl: 'https://www.santander.co.uk/',
    billKeys: ['mortgage'],
    keywords: ['santander'],
  },
  {
    label: 'Barclays',
    portalUrl: 'https://www.barclays.co.uk/',
    billKeys: ['mortgage'],
    keywords: ['barclays'],
  },
  {
    label: 'NatWest',
    portalUrl: 'https://www.natwest.com/',
    billKeys: ['mortgage'],
    keywords: ['natwest'],
  },
  {
    label: 'HSBC',
    portalUrl: 'https://www.hsbc.co.uk/',
    billKeys: ['mortgage'],
    keywords: ['hsbc'],
  },
  {
    label: 'Lloyds',
    portalUrl: 'https://www.lloydsbank.com/',
    billKeys: ['mortgage'],
    keywords: ['lloyds'],
  },
  {
    label: 'PureGym',
    portalUrl: 'https://www.puregym.com/',
    billKeys: ['gym-membership'],
    keywords: ['puregym', 'pure gym'],
  },
  {
    label: 'The Gym Group',
    portalUrl: 'https://www.thegymgroup.com/',
    billKeys: ['gym-membership'],
    keywords: ['the gym group', 'gym group'],
  },
  {
    label: 'Nuffield Health',
    portalUrl: 'https://www.nuffieldhealth.com/',
    billKeys: ['gym-membership'],
    keywords: ['nuffield'],
  },
  {
    label: 'David Lloyd Clubs',
    portalUrl: 'https://www.davidlloyd.co.uk/',
    billKeys: ['gym-membership', 'sports-leisure-clubs'],
    keywords: ['david lloyd'],
  },
  {
    label: 'Bannatyne',
    portalUrl: 'https://www.bannatyne.co.uk/',
    billKeys: ['gym-membership'],
    keywords: ['bannatyne'],
  },
];

const SHARED_HOME_DEFAULT_PORTAL_URLS: Partial<Record<SharedHomeBillKey, string>> = {
  'car-tax': 'https://www.gov.uk/vehicle-tax',
  'car-mot': 'https://www.gov.uk/getting-an-mot',
  'child-maintenance': 'https://www.gov.uk/child-maintenance-service',
};

const SHARED_HOME_BILL_KEYWORDS: Record<SharedHomeBillKey, string[]> = {
  'council-tax': ['council tax'],
  gas: [' gas '],
  electricity: ['electricity', ' electric '],
  broadband: ['broadband', 'internet'],
  'mobile-phone': ['mobile', 'phone', 'sim only'],
  'car-tax': ['vehicle tax', 'car tax'],
  'car-service': ['service', 'garage'],
  'car-mot': ['mot'],
  mortgage: ['mortgage', 'home loan'],
  rent: ['rent', 'landlord', 'tenancy'],
  'service-charge': ['service charge'],
  'ground-rent': ['ground rent'],
  water: ['water'],
  'child-care-education': ['nursery', 'childcare', 'tuition', 'school fee', 'after school'],
  'child-maintenance': ['child maintenance'],
  'gym-membership': ['gym', 'fitness'],
  'sports-leisure-clubs': ['club', 'leisure', 'swim', 'dance', 'football', 'tennis'],
};

const WEEKDAY_LABELS: SharedHomeBillWeekday[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addYears(date: Date, years: number) {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + years);
  return next;
}

function addMonths(date: Date, months: number) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

function isoDate(date: Date) {
  const normalized = startOfDay(date);
  const year = normalized.getFullYear();
  const month = String(normalized.getMonth() + 1).padStart(2, '0');
  const day = String(normalized.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function nextWeekdayOnOrAfter(date: Date, weekday: SharedHomeBillWeekday) {
  const start = startOfDay(date);
  const target = WEEKDAY_LABELS.indexOf(weekday);
  const offset = (target - start.getDay() + 7) % 7;
  return addDays(start, offset);
}

function normalizeText(value: string | null | undefined) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseIsoDate(value: string | null | undefined) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function dayDifference(left: string, right: string) {
  const leftDate = parseIsoDate(left);
  const rightDate = parseIsoDate(right);
  if (!leftDate || !rightDate) return null;
  return Math.round((rightDate.getTime() - leftDate.getTime()) / (1000 * 60 * 60 * 24));
}

function average(values: number[]) {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function mostCommonValue<T extends string | number>(values: T[]) {
  const counts = new Map<T, number>();
  values.forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
  return [...counts.entries()].sort((left, right) => right[1] - left[1])[0]?.[0] ?? null;
}

function formatMonthDay(value: string) {
  const date = parseIsoDate(value);
  if (!date) return value;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(date);
}

function inferRecurringFrequency(dates: string[]) {
  if (dates.length < 2) return null;

  const sorted = [...dates].sort();
  const gaps = sorted
    .slice(1)
    .map((date, index) => dayDifference(sorted[index], date))
    .filter((value): value is number => value != null && value > 0);

  if (!gaps.length) return null;

  const averageGap = average(gaps);
  if (averageGap == null) return null;

  if (averageGap >= 5 && averageGap <= 10) return 'weekly' as const;
  if (averageGap >= 20 && averageGap <= 40) return 'monthly' as const;
  if (averageGap >= 300 && averageGap <= 390) return 'annual' as const;
  return null;
}

function buildRecurringSchedule(dates: string[], frequency: SharedHomeBillFrequency) {
  const parsedDates = dates.map((value) => parseIsoDate(value)).filter((value): value is Date => Boolean(value));
  if (!parsedDates.length) {
    return {
      dueDayOfWeek: null,
      dueDayOfMonth: null,
      annualDueDate: null,
    };
  }

  if (frequency === 'weekly') {
    const weekday = mostCommonValue(parsedDates.map((date) => WEEKDAY_LABELS[date.getDay()])) as SharedHomeBillWeekday | null;
    return {
      dueDayOfWeek: weekday,
      dueDayOfMonth: null,
      annualDueDate: null,
    };
  }

  if (frequency === 'monthly') {
    return {
      dueDayOfWeek: null,
      dueDayOfMonth: mostCommonValue(parsedDates.map((date) => date.getDate())),
      annualDueDate: null,
    };
  }

  const mostRecent = parsedDates.sort((left, right) => right.getTime() - left.getTime())[0];
  const nextDue = startOfDay(addYears(mostRecent, mostRecent < startOfDay(new Date()) ? 1 : 0));
  return {
    dueDayOfWeek: null,
    dueDayOfMonth: null,
    annualDueDate: isoDate(nextDue),
  };
}

function summarizeBillInference(
  providerName: string | null,
  frequency: SharedHomeBillFrequency,
  schedule: Pick<SharedHomeBillRecord, 'dueDayOfWeek' | 'dueDayOfMonth' | 'annualDueDate'>,
) {
  const prefix = providerName ? `Connected bank pattern matched ${providerName}. ` : 'Connected bank pattern matched this bill. ';
  if (frequency === 'weekly') {
    return `${prefix}Suggested a weekly payment${schedule.dueDayOfWeek ? ` every ${schedule.dueDayOfWeek}` : ''}.`;
  }

  if (frequency === 'annual') {
    return `${prefix}Suggested an annual payment${schedule.annualDueDate ? ` around ${formatMonthDay(schedule.annualDueDate)}` : ''}.`;
  }

  return `${prefix}Suggested a monthly payment${schedule.dueDayOfMonth ? ` on the ${schedule.dueDayOfMonth}${ordinalSuffix(schedule.dueDayOfMonth)}` : ''}.`;
}

function ordinalSuffix(value: number) {
  const remainder = value % 100;
  if (remainder >= 11 && remainder <= 13) return 'th';
  if (value % 10 === 1) return 'st';
  if (value % 10 === 2) return 'nd';
  if (value % 10 === 3) return 'rd';
  return 'th';
}

function matchProviderDefinition(value: string | null | undefined, billKey?: SharedHomeBillKey | null) {
  const normalized = normalizeText(value);
  if (!normalized) return null;

  return SHARED_HOME_BILL_PROVIDER_CATALOG.find((provider) => {
    if (billKey && !provider.billKeys.includes(billKey)) return false;
    return provider.keywords.some((keyword) => normalized.includes(normalizeText(keyword)));
  }) || null;
}

function getBillKeysFromText(value: string) {
  const normalized = ` ${normalizeText(value)} `;
  return SHARED_HOME_BILL_DEFINITIONS
    .filter((definition) => SHARED_HOME_BILL_KEYWORDS[definition.key].some((keyword) => normalized.includes(keyword)))
    .map((definition) => definition.key);
}

export function getSharedHomeRoutineCadence(kind: SharedHomeRoutineKind, referenceDate = new Date()): SharedHomeRoutineCadence {
  const month = referenceDate.getMonth();

  if (kind === 'houseplants') {
    if (month >= 2 && month <= 8) {
      return {
        days: 7,
        label: 'Weekly in spring / summer',
        summary: 'Plants usually need a steadier weekly check while growth is faster and rooms are warmer.',
      };
    }

    return {
      days: 14,
      label: 'Fortnightly in autumn / winter',
      summary: 'Plants usually dry out more slowly through the colder months, so a fortnightly check is calmer.',
    };
  }

  if (kind === 'lawn') {
    if (month >= 3 && month <= 8) {
      return {
        days: 7,
        label: 'Weekly in the growing season',
        summary: 'Grass grows fastest through the main season, so a weekly reminder keeps the lawn from turning into a bigger job.',
      };
    }

    if (month === 2 || month === 9) {
      return {
        days: 14,
        label: 'Fortnightly in shoulder months',
        summary: 'Growth is slower at the start and end of the season, so a fortnightly reminder is usually enough.',
      };
    }

    return {
      days: 30,
      label: 'Monthly winter check',
      summary: 'Winter mowing is usually occasional, so Adulting keeps it as a light monthly check rather than a weekly nag.',
    };
  }

  if (kind === 'hoe-weed-garden') {
    if (month >= 2 && month <= 8) {
      return {
        days: 14,
        label: 'Fortnightly in the growing season',
        summary: 'Weeds and beds usually need a steadier check through spring and summer before they turn into a bigger job.',
      };
    }

    return {
      days: 30,
      label: 'Monthly off-season check',
      summary: 'Outside the growing season, a lighter monthly sweep is usually enough.',
    };
  }

  const fixedCadence: Record<
    Exclude<SharedHomeRoutineKind, 'houseplants' | 'lawn' | 'hoe-weed-garden'>,
    SharedHomeRoutineCadence
  > = {
    'change-bedding': {
      days: 14,
      label: 'Fortnightly rhythm',
      summary: 'A fortnightly reminder keeps bedding fresh without making the whole job feel bigger than it is.',
    },
    'change-towels': {
      days: 7,
      label: 'Weekly rhythm',
      summary: 'A weekly towel change is usually enough to keep the bathroom reset without another mental note.',
    },
    laundry: {
      days: 7,
      label: 'Weekly rhythm',
      summary: 'A weekly laundry check helps stop washing from quietly turning into a backlog.',
    },
    ironing: {
      days: 14,
      label: 'Fortnightly rhythm',
      summary: 'A calmer fortnightly nudge suits most homes better than pretending ironing needs a daily slot.',
    },
    'clean-bathrooms': {
      days: 7,
      label: 'Weekly rhythm',
      summary: 'Bathrooms usually stay easier to keep on top of with a weekly clean instead of a bigger catch-up.',
    },
    'clean-surfaces': {
      days: 7,
      label: 'Weekly rhythm',
      summary: 'A weekly surfaces reset keeps the home feeling calmer without becoming a constant chore.',
    },
    'vacuum-mop-floors': {
      days: 7,
      label: 'Weekly rhythm',
      summary: 'A weekly floor pass usually keeps dust and mess manageable before it becomes a bigger weekend job.',
    },
    'clean-kitchen': {
      days: 7,
      label: 'Weekly rhythm',
      summary: 'A weekly kitchen reset keeps the practical areas from getting grim without needing a full deep clean every time.',
    },
    'clean-oven': {
      days: 90,
      label: 'Quarterly rhythm',
      summary: 'An occasional quarterly nudge is usually enough to stop the oven being forgotten until it becomes a much bigger job.',
    },
    'clean-fridge': {
      days: 30,
      label: 'Monthly rhythm',
      summary: 'A monthly fridge clean helps catch old food and spills before they become another low-level stress point.',
    },
    'windows-mirrors-picture-frames': {
      days: 30,
      label: 'Monthly rhythm',
      summary: 'A monthly wipe keeps glass and frames from becoming something you only notice once they are already bothering you.',
    },
    'wash-car': {
      days: 30,
      label: 'Monthly rhythm',
      summary: 'A monthly reminder is enough for most households to keep on top of an outside wash without overdoing it.',
    },
    'valet-inside-car': {
      days: 60,
      label: 'Every two months',
      summary: 'A slightly less frequent interior clean is usually more realistic than pretending it happens every week.',
    },
  };

  return fixedCadence[kind];
}

export function getSharedHomeRoutineDefaultFrequency(kind: SharedHomeRoutineKind, referenceDate = new Date()): SharedHomeRoutineFrequency {
  const cadence = getSharedHomeRoutineCadence(kind, referenceDate);

  if (cadence.days <= 7) return 'weekly';
  if (cadence.days <= 14) return 'fortnightly';
  return 'monthly';
}

export function formatSharedHomeRoutineFrequency(value: SharedHomeRoutineFrequency) {
  return SHARED_HOME_ROUTINE_FREQUENCY_OPTIONS.find((option) => option.value === value)?.label || 'Weekly';
}

export function formatSharedHomeRoutineReminderSchedule(
  frequency: SharedHomeRoutineFrequency,
  reminderDayOfWeek: SharedHomeBillWeekday | null | undefined,
) {
  const frequencyLabel = formatSharedHomeRoutineFrequency(frequency);
  return reminderDayOfWeek ? `${frequencyLabel} on ${reminderDayOfWeek}` : frequencyLabel;
}

export function buildSharedHomeRoutineDueDate(input: {
  frequency: SharedHomeRoutineFrequency;
  reminderDayOfWeek: SharedHomeBillWeekday;
  referenceDate?: Date;
  mode?: 'initial' | 'next';
}) {
  const referenceDate = input.referenceDate || new Date();
  const anchor = startOfDay(referenceDate);

  if (input.mode === 'initial') {
    return isoDate(nextWeekdayOnOrAfter(anchor, input.reminderDayOfWeek));
  }

  const intervalStart = input.frequency === 'weekly'
    ? addDays(anchor, 7)
    : input.frequency === 'fortnightly'
      ? addDays(anchor, 14)
      : addMonths(anchor, 1);

  return isoDate(nextWeekdayOnOrAfter(intervalStart, input.reminderDayOfWeek));
}

export function getSharedHomeRoutineMeta(kind: SharedHomeRoutineKind): SharedHomeRoutineMeta {
  const sharedMeta: Record<SharedHomeRoutineKind, SharedHomeRoutineMeta> = {
    'change-bedding': {
      title: 'Change bedding',
      inactiveBody: 'Turn this on if bedding changes are the kind of job that is easy to leave a bit too long.',
      enableLabel: 'Turn on bedding reminder',
      subtitle: 'Bedroom routine',
      emoji: '🛏️',
    },
    'change-towels': {
      title: 'Change towels',
      inactiveBody: 'Turn this on if towel changes are the kind of small reset that slips when the week gets busy.',
      enableLabel: 'Turn on towel reminder',
      subtitle: 'Bathroom routine',
      emoji: '🧺',
    },
    laundry: {
      title: 'Laundry',
      inactiveBody: 'Turn this on if a regular laundry check helps stop washing from piling up in the background.',
      enableLabel: 'Turn on laundry reminder',
      subtitle: 'Household routine',
      emoji: '🧺',
    },
    ironing: {
      title: 'Ironing',
      inactiveBody: 'Turn this on if ironing is a recurring job you would rather keep visible than keep renegotiating with yourself.',
      enableLabel: 'Turn on ironing reminder',
      subtitle: 'Household routine',
      emoji: '👕',
    },
    'clean-bathrooms': {
      title: 'Clean bathrooms',
      inactiveBody: 'Turn this on if bathroom cleaning is easier to keep steady with a nudge than a bigger catch-up later.',
      enableLabel: 'Turn on bathroom reminder',
      subtitle: 'Cleaning routine',
      emoji: '🛁',
    },
    'clean-surfaces': {
      title: 'Clean surfaces',
      inactiveBody: 'Turn this on if a regular surfaces reset helps the house feel calmer without becoming another constant task.',
      enableLabel: 'Turn on surfaces reminder',
      subtitle: 'Cleaning routine',
      emoji: '🧽',
    },
    'vacuum-mop-floors': {
      title: 'Vacuum / mop floors',
      inactiveBody: 'Turn this on if floors are one of the first things to slip when the rest of life gets noisy.',
      enableLabel: 'Turn on floor reminder',
      subtitle: 'Cleaning routine',
      emoji: '🧹',
    },
    'clean-kitchen': {
      title: 'Clean kitchen',
      inactiveBody: 'Turn this on if a regular kitchen reset helps stop clutter and grease building up in the background.',
      enableLabel: 'Turn on kitchen reminder',
      subtitle: 'Cleaning routine',
      emoji: '🍽️',
    },
    'clean-oven': {
      title: 'Clean oven',
      inactiveBody: 'Turn this on if oven cleaning is the kind of quarterly job that is easy to forget until it is awful.',
      enableLabel: 'Turn on oven reminder',
      subtitle: 'Deep-clean routine',
      emoji: '🔥',
    },
    'clean-fridge': {
      title: 'Clean fridge',
      inactiveBody: 'Turn this on if a monthly fridge reset helps stop old food and spills from becoming another stress point.',
      enableLabel: 'Turn on fridge reminder',
      subtitle: 'Kitchen routine',
      emoji: '🧊',
    },
    'windows-mirrors-picture-frames': {
      title: 'Windows, mirrors and picture frames',
      inactiveBody: 'Turn this on if a regular wipe helps these jobs stay small instead of becoming a whole-house deep clean.',
      enableLabel: 'Turn on glass reminder',
      subtitle: 'Cleaning routine',
      emoji: '🪟',
    },
    'wash-car': {
      title: 'Wash car',
      inactiveBody: 'Turn this on if a monthly outside wash helps the car stay presentable without another mental note.',
      enableLabel: 'Turn on car wash reminder',
      subtitle: 'Vehicle routine',
      emoji: '🚗',
    },
    'valet-inside-car': {
      title: 'Valet inside of car',
      inactiveBody: 'Turn this on if the inside of the car gets messy quickly and a recurring reset helps keep it usable.',
      enableLabel: 'Turn on interior valet reminder',
      subtitle: 'Vehicle routine',
      emoji: '🚘',
    },
    'hoe-weed-garden': {
      title: 'Hoe & weed garden',
      inactiveBody: 'Turn this on if the garden beds are easier to keep under control with a light recurring check.',
      enableLabel: 'Turn on garden reminder',
      subtitle: 'Garden routine',
      emoji: '🌿',
    },
    houseplants: {
      title: 'Houseplant watering',
      inactiveBody: 'If you have houseplants, Adulting can keep the next watering check matched to the season.',
      enableLabel: 'Turn on plant reminder',
      subtitle: 'Plant watering reminder',
      emoji: '🪴',
    },
    lawn: {
      title: 'Lawn mowing',
      inactiveBody: 'If you have a lawn, Adulting can keep mowing checks visible without turning it into another mental note.',
      enableLabel: 'Turn on lawn reminder',
      subtitle: 'Lawn reminder',
      emoji: '🌱',
    },
  };

  return sharedMeta[kind];
}

export function buildSharedHomeRoutineDefaults(kind: SharedHomeRoutineKind, referenceDate = new Date()) {
  const cadence = getSharedHomeRoutineCadence(kind, referenceDate);
  const meta = getSharedHomeRoutineMeta(kind);
  const frequency = getSharedHomeRoutineDefaultFrequency(kind, referenceDate);
  const reminderDayOfWeek: SharedHomeBillWeekday = 'Saturday';
  const dueDate = buildSharedHomeRoutineDueDate({
    frequency,
    reminderDayOfWeek,
    referenceDate,
    mode: 'initial',
  });

  return {
    title: meta.title,
    dueDate,
    cadence,
    frequency,
    reminderDayOfWeek,
    notificationsEnabled: true,
    calendarSyncEnabled: false,
  };
}

export function buildNextSharedHomeRoutineDueDate(
  kind: SharedHomeRoutineKind,
  completedAt = new Date(),
  overrides?: Partial<Pick<SharedHomeRoutineSettings, 'frequency' | 'reminderDayOfWeek'>>,
) {
  const frequency = overrides?.frequency || getSharedHomeRoutineDefaultFrequency(kind, completedAt);
  const reminderDayOfWeek = overrides?.reminderDayOfWeek || 'Saturday';

  return buildSharedHomeRoutineDueDate({
    frequency,
    reminderDayOfWeek,
    referenceDate: completedAt,
  });
}

export function formatSharedRenewalConnectionType(value: SharedRenewalConnectionType) {
  return SHARED_RENEWAL_CONNECTION_OPTIONS.find((option) => option.value === value)?.label || 'Provider account';
}

export function normalizeVehicleRegistration(value: string) {
  return value.trim().toUpperCase().replace(/\s+/g, ' ');
}

export function getSharedHomeBillProviderOptions(billKey: SharedHomeBillKey) {
  return SHARED_HOME_BILL_PROVIDER_CATALOG
    .filter((provider) => provider.billKeys.includes(billKey))
    .map((provider) => ({
      label: provider.label,
      portalUrl: provider.portalUrl,
    }));
}

export function findSharedHomeBillProvider(providerName: string | null | undefined, billKey?: SharedHomeBillKey | null) {
  return matchProviderDefinition(providerName, billKey);
}

export function getSharedHomeBillPortalUrl(billKey: SharedHomeBillKey, providerName: string | null | undefined) {
  return matchProviderDefinition(providerName, billKey)?.portalUrl || SHARED_HOME_DEFAULT_PORTAL_URLS[billKey] || null;
}

export function buildSharedHomeBillDefaults(context: SharedHomeBillSeedContext): SharedHomeBillRecord[] {
  const renting = context.tenure === 'renting-private' || context.tenure === 'renting-social';
  const owner = context.tenure === 'owner-occupier';

  return SHARED_HOME_BILL_DEFINITIONS.map((definition) => {
    let enabled = false;
    let amount: number | null = null;
    let annualDueDate: string | null = null;
    let providerName: string | null = null;

    if (definition.key === 'council-tax') {
      enabled = Boolean(context.councilTaxBand);
    } else if (definition.key === 'gas') {
      enabled = Boolean(context.utilityGasProvider);
      providerName = context.utilityGasProvider || null;
    } else if (definition.key === 'electricity') {
      enabled = Boolean(context.utilityElectricProvider);
      providerName = context.utilityElectricProvider || null;
    } else if (definition.key === 'broadband') {
      enabled = Boolean(context.utilityBroadbandProvider);
      providerName = context.utilityBroadbandProvider || null;
    } else if (definition.key === 'mobile-phone') {
      enabled = Boolean(context.utilityPhoneProvider);
      providerName = context.utilityPhoneProvider || null;
    } else if (definition.key === 'car-tax') {
      enabled = context.hasVehicle === true;
      annualDueDate = context.vehicleTaxDueDate || null;
    } else if (definition.key === 'car-service') {
      enabled = context.hasVehicle === true;
    } else if (definition.key === 'car-mot') {
      enabled = context.hasVehicle === true;
      annualDueDate = context.vehicleMotDueDate || null;
    } else if (definition.key === 'mortgage') {
      enabled = owner || Boolean(context.mortgageProvider);
      amount = owner ? context.monthlyHousingCost : null;
      providerName = context.mortgageProvider || null;
    } else if (definition.key === 'rent') {
      enabled = renting;
      amount = renting ? context.monthlyHousingCost : null;
      providerName = renting ? context.landlordName || null : null;
    } else if (definition.key === 'service-charge') {
      enabled = context.serviceChargeAmount != null;
      amount = context.serviceChargeAmount;
    } else if (definition.key === 'ground-rent') {
      enabled = context.groundRentAmount != null;
      amount = context.groundRentAmount;
    } else if (definition.key === 'water') {
      enabled = Boolean(context.utilityWaterProvider);
      providerName = context.utilityWaterProvider || null;
    }

    return {
      billKey: definition.key,
      enabled,
      frequency: definition.defaultFrequency,
      amount,
      dueDayOfWeek: null,
      dueDayOfMonth: null,
      annualDueDate,
      providerName,
      providerAccount: null,
    };
  });
}

export function buildSharedHomeBillInferences(
  transactions: SharedHomeBillInferenceInput[],
  currentBills: SharedHomeBillRecord[],
) {
  const recurringMerchantGroups = new Map<string, {
    merchantKey: string;
    merchantLabel: string;
    providerName: string | null;
    sourceText: string;
    dates: string[];
    totalAmount: number;
    count: number;
  }>();

  transactions
    .filter((transaction) => Number.isFinite(transaction.amount) && transaction.amount < 0 && transaction.bookingDate)
    .forEach((transaction) => {
      const label = [transaction.creditorName, transaction.description, transaction.accountInstitutionName]
        .filter(Boolean)
        .join(' ')
        .trim();
      const merchantKey = normaliseMoneyMerchant(label);
      if (!merchantKey) return;

      const providerMatch = matchProviderDefinition(label);
      const current = recurringMerchantGroups.get(merchantKey) || {
        merchantKey,
        merchantLabel: providerMatch?.label || getMoneyMerchantDisplayName(label),
        providerName: providerMatch?.label || null,
        sourceText: normalizeText(label),
        dates: [],
        totalAmount: 0,
        count: 0,
      };

      current.dates.push(transaction.bookingDate);
      current.totalAmount += Math.abs(transaction.amount);
      current.count += 1;
      recurringMerchantGroups.set(merchantKey, current);
    });

  const groups = [...recurringMerchantGroups.values()]
    .filter((group) => group.count >= 2)
    .map((group) => {
      const frequency = inferRecurringFrequency(group.dates);
      if (!frequency) return null;
      return {
        ...group,
        frequency,
        averageAmount: Math.round((group.totalAmount / group.count) * 100) / 100,
        schedule: buildRecurringSchedule(group.dates, frequency),
      };
    })
    .filter((group): group is NonNullable<typeof group> => Boolean(group))
    .sort((left, right) => {
      if (right.count !== left.count) return right.count - left.count;
      return right.totalAmount - left.totalAmount;
    });

  const usedMerchantKeys = new Set<string>();
  const inferenceEntries: Array<[SharedHomeBillKey, SharedHomeBillInference]> = [];

  currentBills.forEach((bill) => {
    const knownProvider = bill.providerName ? matchProviderDefinition(bill.providerName, bill.billKey) : null;
    const matchingGroups = groups.filter((group) => {
      if (usedMerchantKeys.has(group.merchantKey)) return false;
      if (knownProvider) {
        return matchProviderDefinition(group.providerName || group.merchantLabel, bill.billKey)?.label === knownProvider.label
          || normalizeText(group.sourceText).includes(normalizeText(knownProvider.label));
      }
      return false;
    });

    const selectedGroup = matchingGroups[0];
    if (!selectedGroup) return;
    usedMerchantKeys.add(selectedGroup.merchantKey);
    inferenceEntries.push([
      bill.billKey,
      {
        billKey: bill.billKey,
        merchantKey: selectedGroup.merchantKey,
        frequency: selectedGroup.frequency,
        amount: selectedGroup.averageAmount,
        dueDayOfWeek: selectedGroup.schedule.dueDayOfWeek,
        dueDayOfMonth: selectedGroup.schedule.dueDayOfMonth,
        annualDueDate: selectedGroup.schedule.annualDueDate,
        providerName: selectedGroup.providerName || selectedGroup.merchantLabel,
        summary: summarizeBillInference(
          selectedGroup.providerName || selectedGroup.merchantLabel,
          selectedGroup.frequency,
          selectedGroup.schedule,
        ),
      },
    ]);
  });

  groups.forEach((group) => {
    if (usedMerchantKeys.has(group.merchantKey)) return;

    const possibleKeys = new Set<SharedHomeBillKey>();
    const providerMatch = matchProviderDefinition(group.providerName || group.merchantLabel);
    if (providerMatch) {
      providerMatch.billKeys.forEach((billKey) => possibleKeys.add(billKey));
    }
    getBillKeysFromText(group.sourceText).forEach((billKey) => possibleKeys.add(billKey));

    const remainingKeys = [...possibleKeys].filter((billKey) => !inferenceEntries.find(([key]) => key === billKey));
    if (remainingKeys.length !== 1) return;

    const billKey = remainingKeys[0];
    usedMerchantKeys.add(group.merchantKey);
    inferenceEntries.push([
      billKey,
      {
        billKey,
        merchantKey: group.merchantKey,
        frequency: group.frequency,
        amount: group.averageAmount,
        dueDayOfWeek: group.schedule.dueDayOfWeek,
        dueDayOfMonth: group.schedule.dueDayOfMonth,
        annualDueDate: group.schedule.annualDueDate,
        providerName: providerMatch?.label || group.providerName || group.merchantLabel,
        summary: summarizeBillInference(
          providerMatch?.label || group.providerName || group.merchantLabel,
          group.frequency,
          group.schedule,
        ),
      },
    ]);
  });

  return new Map(inferenceEntries);
}

function areSharedHomeBillAmountsEquivalent(currentAmount: number | null, nextAmount: number | null) {
  if (currentAmount == null && nextAmount == null) return true;
  if (currentAmount == null || nextAmount == null) return false;
  return Math.abs(currentAmount - nextAmount) < 0.01;
}

function areSharedHomeBillProvidersEquivalent(currentProvider: string | null, nextProvider: string | null) {
  return normalizeText(currentProvider) === normalizeText(nextProvider);
}

function buildSuggestedHomeBillRecord(
  currentBill: SharedHomeBillRecord,
  inference: SharedHomeBillInference,
): SharedHomeBillRecord {
  return {
    ...currentBill,
    enabled: true,
    frequency: inference.frequency,
    amount: currentBill.amount ?? inference.amount,
    dueDayOfWeek: inference.frequency === 'weekly' ? inference.dueDayOfWeek : null,
    dueDayOfMonth: inference.frequency === 'monthly' ? inference.dueDayOfMonth : null,
    annualDueDate: inference.frequency === 'annual' ? inference.annualDueDate : currentBill.annualDueDate,
    providerName: currentBill.providerName ?? inference.providerName,
  };
}

export function buildSharedHomeBillTransferCandidates(
  currentBills: SharedHomeBillRecord[],
  inferences: Map<SharedHomeBillKey, SharedHomeBillInference>,
) {
  const currentByKey = new Map(currentBills.map((bill) => [bill.billKey, bill]));

  return [...inferences.values()]
    .map((inference) => {
      const currentBill = currentByKey.get(inference.billKey);
      if (!currentBill) return null;

      const suggestedBill = buildSuggestedHomeBillRecord(currentBill, inference);
      const changed =
        !currentBill.enabled
        || currentBill.frequency !== suggestedBill.frequency
        || !areSharedHomeBillAmountsEquivalent(currentBill.amount, suggestedBill.amount)
        || currentBill.dueDayOfWeek !== suggestedBill.dueDayOfWeek
        || currentBill.dueDayOfMonth !== suggestedBill.dueDayOfMonth
        || currentBill.annualDueDate !== suggestedBill.annualDueDate
        || !areSharedHomeBillProvidersEquivalent(currentBill.providerName, suggestedBill.providerName);

      if (!changed) return null;

      return {
        billKey: inference.billKey,
        label: getSharedHomeBillLabel(inference.billKey),
        currentBill,
        suggestedBill,
        summary: inference.summary,
        merchantKey: inference.merchantKey,
      } satisfies SharedHomeBillTransferCandidate;
    })
    .filter((entry): entry is SharedHomeBillTransferCandidate => Boolean(entry))
    .sort((left, right) => {
      const leftAmount = left.suggestedBill.amount || 0;
      const rightAmount = right.suggestedBill.amount || 0;
      return rightAmount - leftAmount;
    });
}

export function applySharedHomeBillInferences(
  defaults: SharedHomeBillRecord[],
  inferences: Map<SharedHomeBillKey, SharedHomeBillInference>,
) {
  return defaults.map((record) => {
    const inferred = inferences.get(record.billKey);
    if (!inferred) return record;

    return buildSuggestedHomeBillRecord(record, inferred);
  });
}

export function mergeSharedHomeBillRecords(
  defaults: SharedHomeBillRecord[],
  stored: Array<Partial<SharedHomeBillRecord> & { billKey: SharedHomeBillKey }>,
) {
  const storedByKey = new Map(stored.map((record) => [record.billKey, record]));

  return defaults.map((record) => {
    const storedRecord = storedByKey.get(record.billKey);
    if (!storedRecord) return record;

    return {
      billKey: record.billKey,
      enabled: storedRecord.enabled ?? record.enabled,
      frequency: storedRecord.frequency ?? record.frequency,
      amount: storedRecord.amount ?? record.amount,
      dueDayOfWeek: storedRecord.dueDayOfWeek ?? record.dueDayOfWeek,
      dueDayOfMonth: storedRecord.dueDayOfMonth ?? record.dueDayOfMonth,
      annualDueDate: storedRecord.annualDueDate ?? record.annualDueDate,
      providerName: storedRecord.providerName ?? record.providerName,
      providerAccount: storedRecord.providerAccount ?? record.providerAccount,
    };
  });
}

export function getSharedHomeBillLabel(key: SharedHomeBillKey) {
  return SHARED_HOME_BILL_DEFINITIONS.find((bill) => bill.key === key)?.label || 'Bill';
}

export function getSharedHomeBillDefinition(key: SharedHomeBillKey) {
  return SHARED_HOME_BILL_DEFINITIONS.find((bill) => bill.key === key) || null;
}

export function formatSharedHomeBillFrequency(value: SharedHomeBillFrequency) {
  return SHARED_HOME_BILL_FREQUENCY_OPTIONS.find((option) => option.value === value)?.label || 'Monthly';
}

function ordinal(value: number) {
  return `${value}${ordinalSuffix(value)}`;
}

function formatIsoDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;

  const [, , month, day] = match;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = Number(month) - 1;
  return `${Number(day)} ${monthNames[monthIndex] || month}`;
}

export function formatSharedHomeBillSchedule(record: Pick<SharedHomeBillRecord, 'frequency' | 'dueDayOfWeek' | 'dueDayOfMonth' | 'annualDueDate'>) {
  if (record.frequency === 'weekly') {
    return record.dueDayOfWeek ? `Paid every ${record.dueDayOfWeek}` : 'Weekly payment day not set';
  }

  if (record.frequency === 'annual') {
    return record.annualDueDate ? `Due ${formatIsoDate(record.annualDueDate)} each year` : 'Annual due date not set';
  }

  return record.dueDayOfMonth ? `Paid on the ${ordinal(record.dueDayOfMonth)}` : 'Monthly payment day not set';
}

export function formatSharedHomeBillAmount(amount: number | null, frequency: SharedHomeBillFrequency) {
  if (amount == null) return null;

  const suffix = frequency === 'weekly' ? '/wk' : frequency === 'annual' ? '/yr' : '/mo';
  return `£${amount.toFixed(2)}${suffix}`;
}

export function getSharedHomeBillMonthlyAmount(record: Pick<SharedHomeBillRecord, 'amount' | 'frequency'>) {
  if (record.amount == null) return null;
  if (record.frequency === 'weekly') return Math.round(((record.amount * 52) / 12) * 100) / 100;
  if (record.frequency === 'annual') return Math.round((record.amount / 12) * 100) / 100;
  return Math.round(record.amount * 100) / 100;
}
